import mapJson from 'jsonpath-mapper';
import { v4 as uuidv4 } from 'uuid';
import { Block } from '@/types/Panel.types';
import formatDate from '@/utils/formatDate';
import { truncateString } from '@/utils/truncateString';
import { AvatarMappings } from '@/components/avatar/Avatar.mapper';
import { CompactDropdownOptionProps } from '@/components/compactDropdown/CompactDropdown';

export const BlockCardMappings = {
  name: 'id',
  description: 'description',
  defaultSelected: {
    $path: 'running',
    $formatting: (running: any) => {
      const branch = running?.branch;
      const status = running?.status;
      if (status?.deployment === 'live') return `${branch}/live`;
      else if (status?.workflow === 'released') return `${branch}/released`;
      else if (status?.workflow === 'draft') return `${branch}/draft`;
    },
  },
  defaultOverridden: {
    $path: 'running',
    $formatting: (running: any) => {
      if (running.default || running === 'none') return false;
      else return true;
    },
  },
  branches: 'branches',
};

export const BlocksPanelMappings = {
  title: () => 'Blocks',
  subtitle: {
    $path: '.',
    $formatting: (el: any) => {
      const numberOfBlocks = el?.blocks.length;
      if (numberOfBlocks > 1) return `${numberOfBlocks} blocks available`;
      else if (numberOfBlocks === 0) return `0 blocks available`;
      else return `1 block available`;
    },
  },
  blocks: {
    $path: '.',
    $formatting: (el: any) => {
      const blocks = el?.blocks;
      let b: Block[] = [];
      blocks?.map((block: any) => {
        const getRunningVersion = (version: any) =>
          version.active || version.selected;

        const getBlockRunningVersion = (branches: any[]) => {
          let runningVersion = 'none';
          branches?.map((branch: any) => {
            const versions = branch?.versions;
            const res = versions?.filter(getRunningVersion)?.[0];
            if (res)
              runningVersion = {
                branch: branch?.id,
                ...res,
              };
          });
          return runningVersion;
        };

        const branches = block?.branches;
        const running = getBlockRunningVersion(branches);

        const mappedBlock: any = mapJson(
          {
            ...block,
            running,
          },
          BlockCardMappings
        );
        b.push(mappedBlock);
      });

      return b;
    },
  },
};

const BranchPropMappings = {
  key: 'id',
  value: 'id',
  active: {
    $path: '.',
    $formatting: (el: any) => {
      let isActive = false;
      const versions = el?.versions;
      versions?.map((v: any) => {
        if (v.active) return (isActive = true);
      });
      return isActive;
    },
  },
  default: {
    $path: '.',
    $formatting: (el: any) => {
      let isDefault = false;
      const versions = el?.versions;
      versions?.map((v: any) => {
        if (v.default) return (isDefault = true);
      });
      return isDefault;
    },
  },
  selected: {
    $path: '.',
    $formatting: (el: any) => {
      let isSelected = false;
      const versions = el?.versions;
      versions?.map((v: any) => {
        if (v.selected) return (isSelected = true);
      });
      return isSelected;
    },
  },
};

export const VersionCardMappings = {
  id: 'id',
  defaultSelected: 'active',
  guid: () => uuidv4(),
  versionNo: 'version.versionNo',
  status: {
    $path: 'status',
    $formatting: (status: any) => {
      if (status?.deployment === 'live') return 'live';
      else if (status.workflow === 'released') return 'released';
      else if (status.workflow === 'draft') return 'draft';
    },
  },
  description: 'source.commit.message',
  gitCommitDate: {
    $path: 'source.commit.dateTime',
    $formatting: (date: string) => formatDate(date),
  },
  gitCommitId: {
    $path: 'source.commit.id',
    $formatting: (commitId: string) => {
      if (commitId?.length >= 9) return truncateString(commitId, 7);
      else return commitId;
    },
  },
  gitCommitUrl: 'source.commit.commitUrl',
  avatar: {
    $path: 'source.commit',
    $formatting: (el: any) => mapJson(el, AvatarMappings),
  },
};

export const VersionsPanelMappings = {
  id: 'name',
  title: 'name',
  description: 'description',
  branches: {
    $path: '.',
    $formatting: (block: any) => {
      const branches: any[] = block?.branches;
      const b: CompactDropdownOptionProps[] = [];

      branches?.map((branch: any) => {
        b.push(mapJson(branch, BranchPropMappings));
      });

      return b;
    },
  },
  versions: {
    $path: '.',
    $formatting: (block: any) => {
      const branches = block?.branches;
      const v: any = [];
      branches?.map((b: any) => {
        b?.versions?.map((ver: any) => {
          v.push({
            ...mapJson(ver, VersionCardMappings),
            id: b.id,
            title: `${b.id}/version-${ver.version?.versionNo}`,
          });
        });
      });
      return v;
    },
  },
};
